import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DataTable from "react-data-table-component";
import axios from "axios";
import DNALoader from "../../components/Loaders/DnaLoader";
import Alerts from "../../components/Alerts/alerts";
import PermissionAccess from "../../PermissionAccess/Access";
import DiscountCouponsService from "../DiscountCoupon/Discountcoupons.service";
import AccountDeletionService from "./AccountDeletionRequests.service";

const B_URL = process.env.REACT_APP_LOGINURL;

const AccountDeletionRequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Pending");
  const [filterType, setFilterType] = useState("");
  const [searchText, setSearchText] = useState("");

  const { authData } = PermissionAccess();

  useEffect(() => {
    fetchRequests(1, pageLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimit, filterStatus, filterType, searchText]);

  const fetchRequests = (pageIndex, limit) => {
    if (!authData?.sessionID) return;
    setLoading(true);
    const offset = Math.max(0, (pageIndex - 1) * limit);

    AccountDeletionService.getAccountDeletionRequests(
      authData.sessionID,
      offset,
      limit,
      filterStatus,
      filterType,
      searchText
    )
      .then((response) => {
        setLoading(false);
        if (response.data?.status === false && response.data?.message === "Unauthorized") {
          Alerts.swalErrorAlert("Session expired. Please login again.");
          return;
        }
        setRequests(response.data.data || []);
        setTotalRows(response.data.count?.count || 0);
      })
      .catch(() => {
        setLoading(false);
        Alerts.swalErrorAlert("Server error, please try again later.");
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchRequests(page, pageLimit);
  };

  const handlePerRowsChange = (newLimit, page) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const handleSoftDelete = async (row) => {
    if (!authData?.sessionID) return;

    if (!row.email) {
      Alerts.swalErrorAlert(
        "This request does not contain an email. Please locate the account manually in Doctors/Patients/Merchants list and delete it from there."
      );
      return;
    }

    const confirmed = await Alerts.swalConfirmDeleteAlert(
      `This will mark the ${row.account_type} account as Deleted and clear sensitive login data. This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setLoading(true);

      if (row.account_type === "Doctor") {
        const doctorResp = await DiscountCouponsService.getAllDoctors(
          authData.sessionID,
          0,
          10,
          row.email,
          "",
          ""
        );
        const doctor =
          (doctorResp.data?.data || []).find(
            (d) =>
              d.email &&
              d.email.toString().toLowerCase() === row.email.toString().toLowerCase()
          ) || null;

        if (!doctor) {
          Alerts.swalErrorAlert(
            "No doctor account found with this email. Please verify the email or delete manually."
          );
          setLoading(false);
          return;
        }

        await DiscountCouponsService.deleteDoctor(
          authData.sessionID,
          doctor._id
        );
      } else if (row.account_type === "Patient") {
        const patientResp = await DiscountCouponsService.getAllPatients(
          authData.sessionID,
          0,
          10,
          null,
          row.email,
          ""
        );
        const patient =
          (patientResp.data?.data || []).find(
            (p) =>
              p.email &&
              p.email.toString().toLowerCase() === row.email.toString().toLowerCase()
          ) || null;

        if (!patient) {
          Alerts.swalErrorAlert(
            "No patient account found with this email. Please verify the email or delete manually."
          );
          setLoading(false);
          return;
        }

        await DiscountCouponsService.deletePatient(
          authData.sessionID,
          patient._id
        );
      } else if (row.account_type === "Pharmacy" || row.account_type === "Merchant") {
        const payload = {
          functionName: "GetMerchants",
          postData: {
            offset: 0,
            sort: { _id: -1 },
            limit: 20,
            text: row.email,
            noActiveMatch: true,
          },
        };
        const headers = {
          "Content-Type": "application/json",
          token: authData.sessionID,
        };
        const merchantResp = await axios.post(`${B_URL}GetMerchants`, payload, {
          headers,
        });
        const merchant =
          (merchantResp.data?.data || []).find((m) => {
            const loginEmail = m.loginEmail
              ? m.loginEmail.toString().toLowerCase()
              : "";
            const contactEmail = m.contactEmail
              ? m.contactEmail.toString().toLowerCase()
              : "";
            const rowEmail = row.email.toString().toLowerCase();
            return loginEmail === rowEmail || contactEmail === rowEmail;
          }) || null;

        if (!merchant) {
          Alerts.swalErrorAlert(
            "No pharmacy account found with this email. Please verify the email or delete manually."
          );
          setLoading(false);
          return;
        }

        const deletePayload = {
          functionName: "DeleteMerchant",
          postData: { merchant_id: merchant._id },
        };
        await axios.post(`${B_URL}DeleteMerchant`, deletePayload, {
          headers,
        });
      }

      await AccountDeletionService.updateAccountDeletionRequestStatus(
        authData.sessionID,
        row._id,
        "Processed",
        "Account soft-deleted via admin panel."
      );

      Alerts.swalSuccessAlert("Account deletion request processed successfully.");
      fetchRequests(currentPage, pageLimit);
    } catch (error) {
      console.error("Soft delete error:", error);
      Alerts.swalErrorAlert("Failed to process deletion. Please try again.");
      setLoading(false);
    }
  };

  const handleReject = async (row) => {
    if (!authData?.sessionID) return;

    const confirmed = await Alerts.swalConfirmGeneralAlert(
      "Are you sure you want to mark this account deletion request as Rejected? The user's account will remain active."
    );
    if (!confirmed) return;

    try {
      setLoading(true);

      await AccountDeletionService.updateAccountDeletionRequestStatus(
        authData.sessionID,
        row._id,
        "Rejected",
        "Account deletion request rejected by admin."
      );

      Alerts.swalSuccessAlert("Request has been marked as Rejected.");
      fetchRequests(currentPage, pageLimit);
    } catch (error) {
      console.error("Reject request error:", error);
      Alerts.swalErrorAlert("Failed to reject the request. Please try again.");
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "Date",
      selector: (row) =>
        row.datetime_added ? new Date(row.datetime_added).toLocaleString() : "",
      width: "15%",
      sortable: true,
    },
    {
      name: "Account Type",
      selector: (row) => row.account_type,
      width: "10%",
      sortable: true,
      cell: (row) => (
        <span
          className={`badge ${
            row.account_type === "Doctor" ? "bg-primary" : "bg-success"
          }`}
        >
          {row.account_type === "Merchant" ? "Pharmacy" : row.account_type}
        </span>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.full_name,
      width: "16%",
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      width: "18%",
      sortable: true,
    },
    {
      name: "Reason",
      selector: (row) => row.reason,
      width: "20%",
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      width: "10%",
      cell: (row) => (
        <span
          className={`badge ${
            row.status === "Processed"
              ? "bg-success"
              : row.status === "Rejected"
              ? "bg-danger"
              : "bg-warning text-dark"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Action",
      width: "22%",
      cell: (row) => (
        <div className="d-flex gap-2">
          {row.status === "Pending" && (
            <>
              <Button
                size="sm"
                color="danger"
                onClick={() => handleSoftDelete(row)}
              >
                Delete
              </Button>
              <Button
                size="sm"
                color="secondary"
                onClick={() => handleReject(row)}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="app-page-title">
            <div className="page-title-wrapper">
              <div className="page-title-heading fw-bold">
                <div className="page-title-icon">
                  <i className="pe-7s-trash icon-gradient bg-tempting-azure" />
                </div>
                <div>
                  Account Deletion Requests
                  <div className="page-title-subheading">
                    Review and process account deletion requests from vSARA
                    Doctor, Patient, and Pharmacy apps.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Row>
            <LoadingOverlay
              tag="div"
              active={loading}
              spinner={<DNALoader />}
            >
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <div className="d-flex mb-3 justify-content-between">
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name, email"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "260px" }}
                        />
                        <select
                          className="form-select"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                          style={{ width: "180px" }}
                        >
                          <option value="">All account types</option>
                          <option value="Doctor">Doctor</option>
                          <option value="Patient">Patient</option>
                          <option value="Pharmacy">Pharmacy</option>
                        </select>
                        <select
                          className="form-select"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          style={{ width: "160px" }}
                        >
                          <option value="">All statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="Processed">Processed</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                    </div>

                    <DataTable
                      data={requests}
                      columns={columns}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationResetDefaultPage={resetPaginationToggle}
                    />
                  </CardBody>
                </Card>
              </Col>
            </LoadingOverlay>
          </Row>
        </div>
      </div>
    </Fragment>
  );
};

export default AccountDeletionRequestsList;

